import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { setAppStore, useAppStore } from '@/store';
import useLibrarySectionsQuery from '@/hooks/queries/useLibrarySectionsQuery';
import useHomeLayoutExperiment from '@/hooks/useHomeLayoutExperiment';
import { usePreset } from '@/hooks/useLibrary';
import usePresetFeedAnalytics from '@/hooks/usePresetFeedAnalytics';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getCreditsBalance from '@/utils/analytics/getCreditsBalance';
import trackPresetCredits from '@/utils/analytics/trackPresetCredits';
import { muteCurrentVideo } from '@/utils/media/videoMuteCoordinator';
import { AiGenScreenAction, ClickItem, ScreenType } from '@/types/analytics';
import CategoryRowsSkeleton from '@/components/CategoryRowsSkeleton';
import CategoryRow from '@/components/CategoryRow';
import SeeAllCategory from '@/components/SeeAllCategory';
import TrendingCarousel from '@/components/TrendingCarousel';
import HomeToolTabs from '@/components/HomeToolTabs';
import HomeToolPanel from '@/components/HomeToolPanel';
import { HOME_FEED_SLUG } from '@/store/homeTabs/types';
import pinDeeplinkSections from '@/utils/ui/pinDeeplinkSections';
import pinOnboardingSections from '@/utils/ui/pinOnboardingSections';
import { useOnboardingState } from '@/features/onboarding';

import useSeeAllNavigation from './useSeeAllNavigation';
import useStyles from './styles';
import type { CategoryRowsHomeProps } from './types';
import useMobilePresetFeed from '@/hooks/useMobilePresetFeed';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';

const CategoryRowsHome = ({ hideTrending = false }: CategoryRowsHomeProps) => {
  const classes = useStyles();
  const { itemsPerRow } = useHomeLayoutExperiment();
  const { isPresetFeedActive } = useMobilePresetFeed();
  const isPortfolioApp = checkIsPortfolioApp();

  const { loadPreset } = usePreset();
  const { sendPresetFeedOpenAnalytics } = usePresetFeedAnalytics();
  const { data: sectionsData, isLoading } = useLibrarySectionsQuery();
  const { data: onboarding, isLoading: isOnboardingLoading } =
    useOnboardingState();
  const activeHomeTab = useAppStore((store) => store.activeHomeTab);
  const hideHomeTabs = useAppStore((store) => store.hideHomeTabs);
  const screen = useAppStore((store) => store.screen);
  const isFeedActive = activeHomeTab === HOME_FEED_SLUG;

  const isInitialLoaded =
    !isLoading && !isOnboardingLoading && (sectionsData?.length ?? 0) > 0;

  const nonEmptySections = useMemo(
    () => (sectionsData ?? []).filter((section) => section.items.length > 0),
    [sectionsData]
  );

  const { seeAllSection, openSeeAll } = useSeeAllNavigation({
    sections: nonEmptySections,
  });

  const preferredSections = useMemo(
    () =>
      pinOnboardingSections({
        sections: nonEmptySections,
        categories: onboarding?.categories ?? [],
      }),
    [nonEmptySections, onboarding]
  );

  const orderedSections = useMemo(
    () => pinDeeplinkSections(preferredSections),
    [preferredSections]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef(0);

  // `display: none` collapses the scroller, so the position has to be tracked
  // as it changes and reapplied once the layer is shown again.
  const handleFeedScroll = useCallback(() => {
    savedScrollTopRef.current = containerRef.current?.scrollTop ?? 0;
  }, []);

  // `screen` is a trigger, not a value: returning from the preset feed re-shows
  // this layer without changing any of the conditions below.
  useLayoutEffect(() => {
    if (!seeAllSection && isFeedActive && containerRef.current) {
      containerRef.current.scrollTop = savedScrollTopRef.current;
    }
  }, [seeAllSection, isFeedActive, screen]);

  const handlePresetClick = useCallback(
    (presetSlug: string, categorySlug: string) => {
      muteCurrentVideo();
      setAppStore({
        activeTabSlug: categorySlug,
        presetFeedEntrySlug: presetSlug,
      });
      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.CLICK,
        screen_type: ScreenType.CHOOSER,
        item: ClickItem.Effect,
        settings: {
          category: categorySlug,
          id: presetSlug,
          credits_balance: getCreditsBalance(),
        },
      });

      if (isPresetFeedActive) {
        sendPresetFeedOpenAnalytics({
          slug: presetSlug,
          category: categorySlug,
        });
      }
      trackPresetCredits({ slug: presetSlug, category: categorySlug });
      loadPreset(presetSlug);
    },
    [loadPreset, sendPresetFeedOpenAnalytics, isPresetFeedActive]
  );

  const handleSeeAll = useCallback(
    (category: string) => {
      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.SEE_ALL,
        screen_type: ScreenType.CHOOSER,
        item: ClickItem.Category,
        settings: { category },
      });
      openSeeAll(category);
    },
    [openSeeAll]
  );

  if (seeAllSection) {
    return (
      <SeeAllCategory
        slug={seeAllSection.slug}
        items={seeAllSection.items}
        onPresetClick={handlePresetClick}
      />
    );
  }

  if (!isInitialLoaded) {
    return <CategoryRowsSkeleton itemsPerRow={itemsPerRow} />;
  }

  return (
    <div className={classes.root}>
      {!hideHomeTabs && <HomeToolTabs />}
      <div className={classes.tabContent}>
        <div
          ref={containerRef}
          className={classes.feedLayer}
          hidden={!isFeedActive}
          data-testid="home-gallery"
          onScroll={handleFeedScroll}
        >
          {!hideTrending && isPortfolioApp && <TrendingCarousel />}
          {orderedSections.map(({ slug, title, items }) => (
            <CategoryRow
              key={slug}
              category={slug}
              title={title}
              items={items}
              itemsPerRow={itemsPerRow}
              onPresetClick={handlePresetClick}
              onSeeAll={handleSeeAll}
            />
          ))}
        </div>
        <HomeToolPanel activeSlug={activeHomeTab} />
      </div>
    </div>
  );
};

export default CategoryRowsHome;
